import Flex from '@react-css/flex';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Badge } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import { SettingsDescription, SettingsSectionTitle } from 'containers/SettingsPage/SettingsLayout';
import ConfirmationModal from 'components/Common/ConfirmationModal';
import FormField from 'components/Common/FormField';
import Spinner from 'components/Common/Spinner';
import useAlertQueue from 'hooks/useAlertQueue';
import { useMessage } from 'components/Common/Message';
import type { AppDispatch } from 'store';
import { fetchGettingStarted } from 'store/slices/getStarted';
import { getCurrentUserAndCompany } from 'store/slices/me';
import { updateUserCompany } from 'store/slices/settings/company';
import Body from 'storybookConfig/stories/molecules/Body';
import { ButtonKinds } from 'storybookConfig/stories/molecules/Button';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import SecondaryButton from 'storybookConfig/stories/molecules/Button/SecondaryButton';
import Label from 'storybookConfig/stories/molecules/Label';
import SupportLink from 'storybookConfig/stories/molecules/Link/SupportLink';
import Select from 'storybookConfig/stories/molecules/Select';
import type { Company } from 'types/models/company';
import { installEasyPost, uninstallEasyPost, type InstallEasyPostParams } from 'utils/api/easypost';

type ShippingLabelOption = { label: string; value: string; description: string };

const SHIPPING_LABEL_OWNER_OPTIONS: ShippingLabelOption[] = [
  { label: 'Your Company', value: 'buyer', description: 'You create and pay for labels.' },
  { label: 'Supplier', value: 'seller', description: 'The supplier creates and pays for labels.' },
];

const formatShippingLabelOption = (
  { label, description }: ShippingLabelOption,
  { context }: { context: 'menu' | 'value' }
) =>
  context === 'menu' ? (
    <Flex column gap="2px">
      <span>{label}</span>
      <Body variant="Body/Body Small" color="bodyTextSecondary">
        {description}
      </Body>
    </Flex>
  ) : (
    <span>{label}</span>
  );

interface EasyPostSettingsFormProps {
  connected: boolean;
  onSuccess: () => void;
}

type FormValues = {
  apiKey: string;
};

const EasyPostSettingsForm = ({ connected, onSuccess }: EasyPostSettingsFormProps) => {
  const alertQueue = useAlertQueue();
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<FormValues>();
  const [showUninstallModal, setShowUninstallModal] = useState(false);

  const install = useMutation({
    mutationFn: (params: InstallEasyPostParams) => installEasyPost(params),
    onSuccess: () => {
      alertQueue.addSuccessAlert('Success', 'EasyPost API Key updated successfully');
      onSuccess();
    },
    onError: (error: any) => {
      console.error('Unable to install EasyPost', error);
      alertQueue.addErrorAlert('Something went wrong', error.message);
    },
  });

  const uninstall = useMutation({
    mutationFn: () => uninstallEasyPost(),
    onSuccess: () => {
      alertQueue.addSuccessAlert('Success', 'EasyPost uninstalled successfully');
      onSuccess();
    },
    onError: (error: any) => {
      console.error('Unable to uninstall EasyPost', error);
      alertQueue.addErrorAlert('Something went wrong', error.message);
    },
  });

  return (
    <>
      <ConfirmationModal
        show={showUninstallModal}
        title="Are you sure?"
        confirmText="Uninstall"
        cancelText="Back"
        onConfirm={() => {
          uninstall.mutate();
        }}
        onCancel={() => {
          setShowUninstallModal(false);
        }}
        confirmButtonKind={ButtonKinds.Destructive}
      >
        <Body>
          Are you sure you want to uninstall EasyPost? This will:
          <ul>
            <li>Remove your EasyPost API Key</li>
            <li>Reset all shipping label responsibility settings</li>
          </ul>
        </Body>
      </ConfirmationModal>

      <form
        onSubmit={handleSubmit((data) => {
          install.mutate(data);
        })}
        data-testid="easypost-settings-form"
      >
        <FormField
          autoComplete="off"
          type="text"
          id="easypost-apikey-input"
          label="EasyPost API Key"
          placeholder="EZAK..."
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...register('apiKey', {
            required: 'API Key is required',
          })}
          error={errors.apiKey}
        />
        <Flex>
          {install.isLoading ? (
            <Flex alignItemsCenter gap="8px">
              <Spinner size="sm" />
              <Body>Connecting EasyPost...</Body>
            </Flex>
          ) : (
            <Flex gap="12px">
              {connected && (
                <SecondaryButton
                  size="small"
                  kind="destructive"
                  onClick={() => {
                    setShowUninstallModal(true);
                  }}
                >
                  Uninstall EasyPost
                </SecondaryButton>
              )}
              <PrimaryButton size="small" type="submit">
                Save API Key
              </PrimaryButton>
            </Flex>
          )}
        </Flex>
      </form>
    </>
  );
};

interface ConnectEasyPostProps {
  dispatch: AppDispatch;
  company: Company | null;
}

const ConnectEasyPost = ({ dispatch, company }: ConnectEasyPostProps) => {
  const connected = company?.easypost?.connected ?? false;
  const [showForm, setShowForm] = useState(!connected);

  useEffect(() => {
    if (!connected) setShowForm(true);
  }, [connected]);
  const alertQueue = useAlertQueue();
  const { showSuccessMessage } = useMessage();

  const shippingLabelOwner =
    SHIPPING_LABEL_OWNER_OPTIONS.find(
      (o) => o.value === company?.shippingSettings?.shippingLabelOwner
    ) ?? null;

  const updateShippingLabelOwner = (event: ShippingLabelOption | null) => {
    const shippingSettingsUpdate = { shippingLabelOwner: event?.value || '' };
    dispatch(updateUserCompany({ shippingSettings: shippingSettingsUpdate })).then(
      (action: any) => {
        if (action.error) {
          alertQueue.addErrorAlert('Something went wrong', action.error.message);
          console.error(action.error);
          return;
        }
        showSuccessMessage({ title: 'Success', message: 'Saved shipping preferences' });
      }
    );
  };

  return (
    <>
      <hr style={{ margin: '24px 0' }} />
      <Flex column data-testid="connect-easypost" gap="8px">
        <SettingsSectionTitle>
          EasyPost API Key
          {connected && (
            <Badge variant="success" pill className="ml-3 mt-n1">
              Connected
            </Badge>
          )}
        </SettingsSectionTitle>
        {!connected && (
          <SettingsDescription>
            We support integration with EasyPost for streamlined shipping label creation using your
            approved carriers. You can create an{' '}
            <a href="https://www.easypost.com/">EasyPost account</a> and enter your API key below.{' '}
            <SupportLink article="anymr8mmb3">More Info</SupportLink>
          </SettingsDescription>
        )}
        {showForm ? (
          <EasyPostSettingsForm
            connected={connected}
            onSuccess={() => {
              setShowForm(false);

              // Tell redux to update company and getting started state now that we're connected
              dispatch(fetchGettingStarted());
              dispatch(getCurrentUserAndCompany());
            }}
          />
        ) : (
          <Flex column gap="8px">
            <Flex>
              <SecondaryButton
                size="small"
                type="button"
                onClick={() => {
                  setShowForm(true);
                }}
              >
                Update API Key
              </SecondaryButton>
            </Flex>
            <SettingsDescription>
              <SupportLink href="https://moderndropship.frontkb.com/en/articles/9719041">
                View EasyPost setup instructions
              </SupportLink>
            </SettingsDescription>
          </Flex>
        )}
      </Flex>
      {connected && (
        <Flex column gap="16px" style={{ marginTop: '24px' }}>
          <SettingsSectionTitle>Shipping Responsibility</SettingsSectionTitle>
          <SettingsDescription>
            You can manage the individual partner settings on the{' '}
            <a
              href="https://app.convictional.com/partners"
              target="_blank"
              rel="noopener noreferrer"
            >
              Suppliers page
            </a>
            .
          </SettingsDescription>
          <Flex column gap="8px">
            <Label htmlFor="shippingLabelOwnership" className="mb-2">
              Who provides the shipping label?
            </Label>
            <Select
              isClearable
              onChange={updateShippingLabelOwner}
              id="shippingLabelOwnership"
              name="shippingLabelOwnership"
              value={shippingLabelOwner}
              options={SHIPPING_LABEL_OWNER_OPTIONS}
              formatOptionLabel={formatShippingLabelOption}
              placeholder="Select..."
            />
          </Flex>
        </Flex>
      )}
    </>
  );
};

export default ConnectEasyPost;
